import { useQuery } from '@tanstack/react-query';
import { useLeagueStore } from '../store/leagueStore';
import apiClient from '../lib/api/apiClient';

export interface Team {
  id: number;
  name: string;
  abbreviation: string;
  city: string;
  state: string;
  logo?: string;
  color_primary?: string;
  color_secondary?: string;
  color_text?: string;
  stadium?: string;
  level?: string;
  won?: number;
  lost?: number;
  color_3?: string;
  price?: number;
  owned_by?: number | null;
  tier?: string;
  prefix?: string;
}

export interface LeagueApi {
  id: number;
  name: string;
  logo?: string;
  year?: number;
  color_primary?: string;
  color_secondary?: string;
  color_text?: string;
  lineup_editing?: string;
  roster_editing?: string;
  is_template?: boolean;
  creation_date?: string;
  commissioners?: string[];
  dh?: string;
  interleague_play?: boolean;
  price_league?: number;
  price_team_high?: number;
  price_team_low?: number;
  description?: string;
  type?: string;
  league_code?: string;
  tier?: string;
  draft_type?: string;
  team_count?: number;
  teams_owned?: number;
  teams: Team[];
  player_count: number;
}

const fetchLeagues = async (): Promise<LeagueApi[]> => {
  const res = await apiClient.get('/leagues');
  return res.data;
};

export const useLeagues = () => {
  const { searchQuery, sortField, sortOrder, page, rowsPerPage } = useLeagueStore();

  const { data: leagues = [], isLoading, error } = useQuery<LeagueApi[]>({
    queryKey: ['leagues'],
    queryFn: fetchLeagues,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // Keep data in cache for 30 minutes
  });

  // Filter and sort leagues
  const filteredLeagues = leagues
    .filter((league: LeagueApi) => {
      // Exclude template leagues
      if (league.is_template) return false;
      if (!searchQuery) return true;
      const searchLower = searchQuery.toLowerCase();
      return (
        league.name.toLowerCase().includes(searchLower) ||
        (league.commissioners && league.commissioners.some((c: string) => c.toLowerCase().includes(searchLower)))
      );
    })
    .sort((a: LeagueApi, b: LeagueApi) => {
      const aValue = a[sortField as keyof LeagueApi];
      const bValue = b[sortField as keyof LeagueApi];

      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortOrder === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

  // Paginate results
  const start = page * rowsPerPage;
  const end = start + rowsPerPage;
  const paginatedLeagues = filteredLeagues.slice(start, end);

  return {
    leagues: paginatedLeagues,
    totalLeagues: filteredLeagues.length,
    isLoading,
    error,
  };
}; 